import React, { useCallback, useEffect, useMemo, useState } from "react";
import ConfirmationModal from "../../components/ConfirmationModal";
import EmailTemplateModal from "./EmailTemplateModal";
import { CollapseSection, ItemColumn, ItemLabel, ItemList } from "./EmailTemplateConst";
import { MobileView, StyledContent, StyledLayout, WebView } from "../Category/styles";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { createEmailTemplate, deleteEmailTemplate, fetchEmailTemplates, updateEmailTemplate, updateEmailTemplateOrder } from "../../redux/features/emailTemplate/emailTemplateAsyncThunk";
import { toast } from "react-toastify";
import { Form } from "antd";
import { selectEmailTemplate } from "../../redux/features/emailTemplate/emailTemplateSlice";
import { EmailTemplate as EmailTemplateType } from "../../types/EmailTemplateTypes";
import { RequestResults } from "../../redux/common/RequestStatus";
import { checkHasPermission } from "../../utils/helpers/checkHasPermission";
import { selectUserRole } from "../../redux/features/auth/selectors";
import { permissions } from "../../utils/constants/permissions";
import { ActivationStatus } from "../../types";
import { DropResult } from "react-beautiful-dnd";

const EmailTemplate: React.FC = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmItem, setShowDeleteConfirmItem] = useState<EmailTemplateType | null>(null);
  const [editingRecord, setEditingRecord] = useState<EmailTemplateType | null>(null);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplateType | null>(null);
  const [showDeactivationConfirm, setShowDeactivationConfirm] = useState<boolean>(false);
  const [currentEmailTemplate, setCurrentEmailTemplate] = useState<EmailTemplateType | null>(null);
  const [emailTemplatesList, setEmailTemplatesList] = useState<EmailTemplateType[]>([]);
  const { data: emailTemplates, status: emailTemplateStatus, loading } = useAppSelector(selectEmailTemplate);
  const userRole = useAppSelector(selectUserRole);

  const hasPermission = useCallback(
    (permissionKey: string) => checkHasPermission(userRole, permissionKey),
    [userRole]
  );

  const isAddPermission = useMemo(() => hasPermission(permissions.AddEditEmailTemplate), [hasPermission]);
  const isEditPermission = useMemo(() => hasPermission(permissions.AddEditEmailTemplate), [hasPermission]);
  const isViewPermission = useMemo(() => hasPermission(permissions.ViewEmailTemplate), [hasPermission]);
  const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteEmailTemplate), [hasPermission]);

  useEffect(() => {
    dispatch(fetchEmailTemplates({ status: null }));
  }, [dispatch]);

  const handleSave = async (values: any) => {
    const fixedValues = {
      ...values,
      status: values.status ? 1 : 0,
    };

    try {
      if (form.getFieldValue("id")) {
        await dispatch(updateEmailTemplate({ ...editingRecord, ...fixedValues })).unwrap();
      } else {
        await dispatch(createEmailTemplate(fixedValues)).unwrap();
      }
      form.resetFields();
      setIsModalOpen(false);
      setEditingRecord(null);
    } catch (error) {
      console.error("Create email template error:", error);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (showDeleteConfirmItem?.id) {
        await dispatch(deleteEmailTemplate({ id: showDeleteConfirmItem.id })).unwrap();
      }
    } catch (error) {
      console.error("delete error: ", error);
    } finally {
      setShowDeleteConfirmItem(null);
      setIsDeleting(false);
    }
  };

  const handleModal = () => {
    setIsModalOpen((prevModal) => !prevModal);
    form.resetFields();
    setEditingRecord(null);
    setCurrentEmailTemplate(null);
    setShowDeactivationConfirm(false);
  };

  const handleStatusChangeConfirm = async () => {
    try {
      if (currentEmailTemplate) {
        const updatedStatus = currentEmailTemplate.status === 1 ? 0 : 1;
        await dispatch(
          updateEmailTemplate({
            ...currentEmailTemplate,
            status: updatedStatus,
          })
        ).unwrap();
        toast.success(`EmailTemplate ${updatedStatus === 1 ? "activated" : "deactivated"} successfully.`);
        setShowDeactivationConfirm(false);
        setCurrentEmailTemplate(null);
      }
    } catch (error: any) {
      console.error("Status change failed:", error);
      toast.error(error || "Failed to change email template status.");
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    try {
      const updatedTemplates = Array.from(emailTemplatesList);
      const [movedItem] = updatedTemplates.splice(result.source.index, 1);
      updatedTemplates.splice(result.destination.index, 0, movedItem);
      setEmailTemplatesList(updatedTemplates);
      // Reassign the index/order
      const reorderedTemplates = updatedTemplates.map((template, index) => ({
        id: template?.id ?? "",
        index,
      }));

      await dispatch(updateEmailTemplateOrder({ orderUpdates: reorderedTemplates })).unwrap();
    } catch (error) {
      console.error("Failed to update email template order:", error);
    }
  };

  useEffect(() => {
    if (emailTemplates) {
      setEmailTemplatesList(emailTemplates);
    }
  }, [emailTemplates]);

  return (
    <StyledLayout>
      <WebView>
        <StyledContent>
          <ItemColumn
            hasAddPermission={isAddPermission}
            hasEditPermission={isEditPermission}
            hasDeletePermission={isDeletePermission}
            hasViewPermission={isViewPermission}
            title="Email Templates"
            buttonLabel="+ Add Templates"
            onAddClick={handleModal}
            loading={emailTemplateStatus === RequestResults.pending}
            dataSource={emailTemplatesList}
            isSelected={selectedEmailTemplate?.id}
            isSelectedItemOnClick={(item: any) => selectedEmailTemplate?.id !== item.id && setSelectedEmailTemplate(item)}
            onEdit={(item: any) => {
              setEditingRecord(item);
              form.setFieldsValue(item);
              setIsModalOpen(true);
            }}
            onDelete={(item: any) => setShowDeleteConfirmItem(item)}
            onChange={(item: any) => {
              setCurrentEmailTemplate(item);
              setShowDeactivationConfirm(true);
            }}
            handleDragEnd={handleDragEnd}
          />
        </StyledContent>
      </WebView>

      <MobileView>
        <CollapseSection
          itemKey="1"
          activeKey="1"
          label={<ItemLabel title="Email Templates" buttonLabel="+ Add Templates" onButtonClick={handleModal} hasAddPermission={isAddPermission} />}
          children={
            <ItemList
              hasAddPermission={isAddPermission}
              hasEditPermission={isEditPermission}
              hasDeletePermission={isDeletePermission}
              hasViewPermission={isViewPermission}
              loading={emailTemplateStatus === RequestResults.pending}
              dataSource={emailTemplatesList}
              isSelected={selectedEmailTemplate?.id}
              isSelectedItemOnClick={(item: any) => selectedEmailTemplate?.id !== item.id && setSelectedEmailTemplate(item)}
              onEdit={(item: any) => {
                setEditingRecord(item);
                form.setFieldsValue(item);
                setIsModalOpen(true);
              }}
              onDelete={(item: any) => setShowDeleteConfirmItem(item)}
              onItemClick={() => { }}
              onChange={(item: any) => {
                setCurrentEmailTemplate(item);
                setShowDeactivationConfirm(true);
              }}
              handleDragEnd={handleDragEnd}
            />
          }
        />
      </MobileView>

      {isModalOpen && <EmailTemplateModal form={form} isModalOpen={isModalOpen} onCancel={handleModal} handleSave={handleSave} hasEditPermission={isEditPermission} hasViewPermission={isViewPermission} hasAddPermission={isAddPermission} />}

      {showDeleteConfirmItem && (
        <ConfirmationModal
          handleSave={handleDelete}
          isModalOpen={!!showDeleteConfirmItem}
          onCancel={() => setShowDeleteConfirmItem(null)}
          title={`Delete ${showDeleteConfirmItem?.longTitle}`}
          message={`Are you sure you want to delete the email template: ${showDeleteConfirmItem?.longTitle}?`}
          confirmButtonText="Delete"
          loading={isDeleting}
        />
      )}

      {showDeactivationConfirm && (
        <ConfirmationModal
          hasAddPermission={isAddPermission}
          hasEditPermission={isEditPermission}
          hasViewPermission={isViewPermission}
          handleSave={handleStatusChangeConfirm}
          isModalOpen={showDeactivationConfirm}
          onCancel={() => setShowDeactivationConfirm(false)}
          title={`Confirm ${currentEmailTemplate?.status === ActivationStatus.Active ? "Deactivation" : "Activation"} Email Template`}
          message={`Are you sure you want to ${currentEmailTemplate?.status === ActivationStatus.Active ? "deactivate" : "activate"} this email template?`}
          confirmButtonText={currentEmailTemplate?.status === ActivationStatus.Active ? "Deactivate" : "Activate"}
          loading={loading}
        />
      )}
    </StyledLayout>
  );
};

export default EmailTemplate;
